import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AssetDto,
  AssetVersionDto,
  CreateAssetBodyDto,
  CreateAssetVersionFromScratchBodyDto,
  MoveAssetVersionBodyDto,
  UpdateAssetBodyDto,
  UpdateAssetVersionBodyDto,
} from '@picflow/types';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  mergeWith,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { multiPartUploadPartSize } from '../consts';
import { sortAssetSection } from '../gallery/lib/sort-asset-section';
import { GROWTHBOOK } from '../growthbook';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SectionService } from './section.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';
import { UnseenActivityService } from './unseen-activity.service';
import { VirusScanService } from './virus-scan.service';

@Injectable({
  providedIn: 'root',
})
export class AssetService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private sectionService = inject(SectionService);
  private tenantService = inject(TenantService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private unseenActivityService = inject(UnseenActivityService);
  private state = inject(StateService);
  private socketService = inject(SocketService);
  private virusScanService = inject(VirusScanService);
  allAssets: Map<string, AssetDto> = new Map();
  updateList: { id: string; galleryId: string; sectionId: string; params: UpdateAssetBodyDto }[] = [];
  deleteList: { id: string; galleryId: string; sectionId: string }[] = [];
  defaultIncludes = '&include[]=is_liked&include[]=tags';

  onNewAssets$ = new BehaviorSubject<AssetDto[]>(null);

  public readonly optimisticUpdates = new Subject<{ name: string; payload: { asset: AssetDto; oldAsset: AssetDto } }>();

  public readonly updates: Observable<{
    id: string;
    action: 'updated' | 'created' | 'deleted';
    asset?: AssetDto;
    previousValue?: AssetDto;
    isOptimistic: boolean;
  }> = this.socketService.onSocketEvent.pipe(
    map(e => ({ ...e, isOptimistic: false })),
    mergeWith(this.optimisticUpdates.pipe(map(e => ({ ...e, isOptimistic: true })))),
    filter(e => {
      if (e?.name === 'asset.updated') return true;
      if (e?.name === 'asset.created') return true;
      if (e?.name === 'asset.deleted') return true;
    }),
    map(event => {
      const asset = event.payload.asset as AssetDto;
      return {
        id: asset.id,
        action: event.name.split('.')[1] as 'updated' | 'created' | 'deleted',
        asset,
        previousValue: event.payload.oldAsset as AssetDto,
        isOptimistic: event.isOptimistic,
      };
    })
  );

  private updates$ = this.updates.subscribe(event => {
    if (!event?.asset) return;

    this.observableLoader.updateFuzzy<AssetDto[]>(`assets_${event.asset.gallery}`, (assets, key) =>
      this.updateAssetsCacheFn(assets, key, event)
    );
    this.observableLoader.updateFuzzy<AssetDto[]>(`assets_${event.asset.section}`, (assets, key) =>
      this.updateAssetsCacheFn(assets, key, event)
    );
    this.observableLoader.update<AssetDto>(`assets_${event.asset.id}`, asset => this.updateAssetCacheFn(asset, event));
  });

  private updateAssetCacheFn(asset: AssetDto, event: { action: 'updated' | 'created' | 'deleted'; asset?: AssetDto }) {
    if (!asset) return;

    if (event.action === 'updated') {
      if (event.asset.is_liked === undefined) {
        delete event.asset.is_liked;
      }
      return {
        ...asset,
        ...event.asset,
      };
    }

    return asset;
  }

  private updateAssetsCacheFn(
    assets: AssetDto[],
    key: string,
    event: { action: 'updated' | 'created' | 'deleted'; asset?: AssetDto }
  ) {
    if (!assets) return;
    const sectionId = 'sec_' + key.split('_').pop();

    if (event.action === 'updated') {
      // Could be because an asset moved from one section to another
      const index = assets.findIndex(a => a.id === event.asset.id);
      if (index !== -1) {
        if (assets[index].section !== event.asset.section) {
          assets.splice(index, 1);
          return assets;
        }
        if (event.asset.is_liked === undefined) {
          delete event.asset.is_liked;
        } else {
          assets[index] = {
            ...assets[index],
            ...event.asset,
          };
        }
      } else if ((event.asset.section as string) === sectionId) {
        assets.push(event.asset);
      }
      return assets;
    }

    if ((event.asset.section as string) !== sectionId) return assets;

    if (event.action === 'deleted') {
      return assets.filter(a => a.id !== event.asset.id);
    } else if (event.action === 'created') {
      if (assets.some(a => a.id === event.asset.id)) return assets;
      assets.push(event.asset);
      return assets;
    }
    return assets;
  }

  private todeleteFromVirusScanner = this.virusScanService.deleteAsset.subscribe(async asset => {
    if (!asset) return;
    this.deleteAsset(asset);
  });

  private growthBook = inject(GROWTHBOOK);

  private getAssetObservableInternal(
    assetId: string,
    galleryId: string,
    data: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ) {
    return this.observableLoader.get(`assets_${assetId}`, () => {
      let url = `${this.config.apiEndpoint}/assets/${assetId}?gallery=${galleryId}${
        data.noIncludes ? '' : this.getIncludes()
      }${this.getPassword(galleryId, true)}`;
      if (data.ignore401Errors) url += '&ignore_401_error=true';
      return this.http.get<AssetDto>(url);
    });
  }

  public getAssetObservable(
    assetId: string,
    galleryId: string,
    options: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ): Observable<AssetDto> {
    return this.getAssetObservableInternal(assetId, galleryId, options).pipe(
      mergeWith(
        this.updates.pipe(
          filter(e => e.id === assetId),
          withLatestFrom(this.getAssetObservableInternal(assetId, galleryId, options)),
          map(([update, asset]) => {
            return {
              ...asset,
              ...update.asset,
            };
          })
        )
      )
    );
  }

  public async getAsset(
    assetId: string,
    galleryId?: string,
    options: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ): Promise<AssetDto> {
    return await firstValueFrom(this.getAssetObservable(assetId, galleryId, options));
  }

  public async getAssetCount(galleryId: string, sectionId: string) {
    return await this.loader.get('assets_count_' + galleryId + sectionId, async () => {
      const resp = await lastValueFrom(
        this.http.get<{ total_count: number }>(
          `${
            this.config.apiEndpoint
          }/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes()}${this.getPassword(
            <string>galleryId,
            true
          )}&limit=1&include=total_count`
        )
      );

      return resp.total_count;
    });
  }

  private async getAssetsInternal(data: {
    sectionId: string;
    galleryId: string;
    options: { ignore401Errors?: boolean };
  }) {
    let url = `${this.config.apiEndpoint}/assets?section=${data.sectionId}&gallery=${
      data.galleryId
    }${this.getIncludes()}${this.getPassword(<string>data.galleryId, true)}`;
    if (data.options.ignore401Errors) {
      url += '&ignore_401_error=true';
    }

    const resp = await lastValueFrom(this.http.get<{ data: AssetDto[] }>(url));
    const assets = resp.data;
    assets.sort((a, b) => a.position - b.position);
    this.onGetNewAssets(assets);
    return assets;
  }

  public getAssetsObservable(sectionId: string, galleryId: string, options: { ignore401Errors?: boolean } = {}) {
    return this.observableLoader.get(`assets_${galleryId}_${sectionId}`, () =>
      from(this.getAssetsInternal({ sectionId, galleryId, options }))
    );
  }

  public getAssetsForGalleryObservable(galleryId: string, options: { ignore401Errors?: boolean } = {}) {
    return this.sectionService.getSections(galleryId as string).pipe(
      switchMap(sections => {
        const assetSections = sections.filter(section => section.object === 'asset');
        if (!assetSections.length) return of([]);
        return combineLatest(
          assetSections.map(section =>
            this.getAssetsObservable(section.id, section.gallery, options).pipe(
              map(assets => sortAssetSection(assets, section.sorting))
            )
          )
        );
      }),
      map(assets => assets.flat())
    );
  }

  public async getAssetsForGallery(
    galleryId: string,
    options: { ignore401Errors?: boolean } = {}
  ): Promise<AssetDto[]> {
    return await firstValueFrom(this.getAssetsForGalleryObservable(galleryId, options));
  }

  public async deleteAsset(asset: AssetDto) {
    if (!asset) return;

    this.deleteList.push({ id: asset.id, galleryId: <string>asset.gallery, sectionId: <string>asset.section });

    await sleep(0);
    return this.handleDeletions();
  }

  private async handleDeletions() {
    if (!this.deleteList.length) return;
    const _deleteList = this.deleteList.slice();
    this.deleteList = [];

    const params = {
      mode: 'sync',
      allSettled: true,
      operations: _deleteList.map(update => ({
        api: 'asset',
        method: 'delete',
        payload: {
          id: update.id,
        },
      })),
    };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    this.tenantService.refreshTenant();
  }

  private async handleUpdates() {
    if (!this.updateList.length) return;
    const _updateList = this.updateList.slice();
    this.updateList = [];
    const params = {
      mode: 'sync',
      allSettled: true,
      operations: _updateList.map(update => ({
        api: 'asset',
        method: 'update',
        payload: {
          id: update.id,
          body: update.params,
        },
      })),
    };

    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    this.removeAssets();
  }

  public async updateAsset(
    asset: AssetDto,
    params: UpdateAssetBodyDto,
    galleryId: string,
    sectionId: string,
    isPublic = false
  ) {
    this.optimisticUpdates.next({
      name: 'asset.updated',
      payload: {
        asset: {
          ...asset,
          ...params,
        },
        oldAsset: asset,
      },
    });

    if (isPublic) {
      // bulk api doesn't work for some reason when used on sub domains (public galleries)
      this.removeAssets();
      return await lastValueFrom(
        this.http.patch(`${this.config.apiEndpoint}/assets/${asset.id}${this.getPassword(galleryId, false)}`, params)
      );
    }

    this.updateList.push({ id: asset.id, params, galleryId, sectionId });

    await sleep(0);
    return this.handleUpdates();
  }

  public async toggleAssetLike(asset: AssetDto, galleryId: string, like: boolean) {
    const url = `${this.config.apiEndpoint}/assets/${asset.id}/like?gallery=${galleryId}${this.getPassword(
      galleryId,
      true
    )}`;

    this.optimisticUpdates.next({
      name: 'asset.updated',
      payload: {
        asset: {
          ...asset,
          ...{ is_liked: like },
        },
        oldAsset: asset,
      },
    });

    if (like) {
      await lastValueFrom(this.http.post(url, {}));
    } else {
      await lastValueFrom(this.http.delete(url));
    }
    this.removeAssets();
  }

  private calcUploadType(contentLength: number) {
    if (contentLength <= multiPartUploadPartSize) return 'post';
    return 'multipart';
  }

  public async postAsset(data: {
    galleryId: string;
    sectionId: string;
    contentLength: number;
    position: number;
    name: string;
    description?: string;
  }): Promise<AssetDto> {
    const params: CreateAssetBodyDto = {
      gallery: data.galleryId,
      section: data.sectionId,
      asset_name: data.name.replace(/#/, ''),
      visibility: 'public',
      position: data.position,
      description: data.description || undefined,
      content_length: data.contentLength,
      upload_type: this.calcUploadType(data.contentLength),
      accelerated: this.growthBook.isOn('accelerated-upload'),
    };
    const resp = await lastValueFrom(this.http.post<AssetDto>(`${this.config.apiEndpoint}/assets`, params));
    this.removeAssets();
    return resp;
  }

  public async postVersion(
    assetId: string,
    data: {
      contentLength: number;
      name: string;
      galleryId: string;
    }
  ) {
    const params: CreateAssetVersionFromScratchBodyDto = {
      asset: assetId,
      asset_name: data.name.replace(/#/, ''),
      content_length: data.contentLength,
      upload_type: this.calcUploadType(data.contentLength),
      accelerated: this.growthBook.isOn('accelerated-upload'),
    };

    const version = await this.calcNextVersionNumber(assetId, data.galleryId);
    params.version_name = `v${version}`;

    const resp = <AssetVersionDto>(
      await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/asset_versions`, params))
    );
    this.removeAssets();
    return resp;
  }

  public async copyAsset(assetId: string, toGalleryId: string, toSectionId: string) {
    const params: CreateAssetBodyDto = {
      copy_from: assetId,
      gallery: toGalleryId,
      section: toSectionId,
    };
    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets`, params));
    this.removeAssets();
    return resp;
  }

  public async copyAssets(assetIds: string[], toGalleryId: string, toSectionId: string) {
    const params = {
      mode: 'sync',
      allSettled: true,
      operations: assetIds.map(assetId => ({
        api: 'asset',
        method: 'create',
        payload: {
          body: {
            copy_from: assetId,
            gallery: toGalleryId,
            section: toSectionId,
          },
        },
      })),
    };

    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    this.removeAssets();
    return resp as { results: AssetDto[] };
  }

  public async makeVersionOutOfAsset(fromAssetId: string, toAssetId: string, toGalleryId: string) {
    const version = await this.calcNextVersionNumber(toAssetId, toGalleryId);
    const params: MoveAssetVersionBodyDto = {
      move_from: fromAssetId,
      move_to: toAssetId,
    };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/asset_versions`, params));
    this.removeAssets();
    const asset = await this.getAsset(toAssetId, toGalleryId);
    await this.updateAssetVersion(asset.version_data.id, { version_name: `v${version}` });
    this.removeAssets();
    this.loader.removeFuzzy('galleries');
    return asset;
  }

  public async calcNextVersionNumber(assetId: string, galleryId: string) {
    const versions = await this.getAssetVersions(assetId, galleryId);

    versions.sort(
      (a, b) => parseInt((a.version_name || 'v1').substring(1)) - parseInt((b.version_name || 'v1').substring(1))
    );

    const version = this.state.plan?.product?.isFree
      ? 1
      : parseInt((versions.pop()?.version_name || 'v1').substring(1)) + 1;

    return version;
  }

  public async deleteAssetVersion(versionId: string) {
    const resp = await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions/${versionId}`));
    this.removeAssets();
    this.tenantService.refreshTenant();
    return resp;
  }

  public async deleteAllAssetVersions(galleryId?: string) {
    if (galleryId) {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/galleries/${galleryId}/asset_versions`));
    } else {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions`));
    }
    this.removeAssets();
    return true;
  }

  public async updateAssetVersion(versionId: string, params: UpdateAssetVersionBodyDto) {
    const resp = await lastValueFrom(
      this.http.patch<AssetVersionDto>(`${this.config.apiEndpoint}/asset_versions/${versionId}`, params)
    );
    this.removeAssets();
    return resp;
  }

  public async handleAssetOrderChange(asset: AssetDto) {
    return this.handleSectionAssetsOrderChange(<string>asset.section, <string>asset.gallery);
  }

  public async handleSectionAssetsOrderChange(sectionId: string, galleryId: string) {
    const assets = await firstValueFrom(this.getAssetsObservable(sectionId, galleryId));
    return this.handleOrderChange(assets, <string>galleryId, <string>sectionId);
  }

  public async handleOrderChange(assets: AssetDto[], galleryId: string, sectionId: string) {
    const changes = this.calcOrderChanges(assets);
    await Promise.all(
      changes.map(change => {
        return this.updateAsset(change.asset, { position: change.pos }, galleryId, sectionId);
      })
    );
    return changes;
  }

  public calcOrderChanges(assets: AssetDto[]): { asset: AssetDto; pos: number }[] {
    // if there are duplicates (positions) we just send the whole set of assets to be changed
    if (assets.some(asset => assets.some(i => i.position === asset.position && i.id !== asset.id))) {
      return assets.map((asset, i) => {
        return { asset, pos: i };
      });
    }

    // Otherwise we figure out the changes and send only those
    const assetsInPreviousOrder = assets.slice().sort((a, b) => a.position - b.position);
    return assets
      .map((asset, i) => {
        const oldAssetInPos = assetsInPreviousOrder[i];
        if (oldAssetInPos.id !== asset.id) return { asset, pos: oldAssetInPos.position };
      })
      .filter(a => a);
  }

  /**
   * Not ready yet. Backend throws an error for some reason
   * @param asset
   */
  public async assetView(asset: AssetDto) {
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets/${asset.id}/view`, {}));
  }

  public async getAssetVersions(assetId: string, galleryId: string): Promise<AssetVersionDto[]> {
    return await this.loader.get('assets_versions_' + assetId, () =>
      this.getAssetVersionsInternal({
        galleryId,
        assetId,
      })
    );
  }

  private async getAssetVersionsInternal(data: { assetId: string; galleryId: string }): Promise<AssetVersionDto[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: AssetVersionDto[] }>(
        `${this.config.apiEndpoint}/asset_versions?asset=${data.assetId}${this.getPassword(data.galleryId, true)}`
      )
    );
    return resp.data;
  }

  private getPassword(galleryId: string, notFirstParam = false) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return `${notFirstParam ? '&' : '?'}password=${encodeURIComponent(password)}`;
  }

  private onGetNewAssets(assets: AssetDto[]) {
    if (!assets) return;
    this.onNewAssets$.next(assets);

    assets.forEach(asset => {
      this.allAssets.set(asset.id, asset);
      if (asset['unseen_activities'] !== undefined) {
        this.state.unseenActivityCountMap.set(asset.id, asset['unseen_activities']);
        delete asset['unseen_activities'];
        this.unseenActivityService.registerAssets(asset);
      }
    });
  }

  private getIncludes() {
    let includes = this.defaultIncludes;
    if (this.state.user) includes += '&include[]=unseen_activities';
    // if (this.state.isPublic) includes += '&status[]=approved&status[]=no_status';
    return includes;
  }

  public removeAssets() {
    this.loader.removeFuzzy('assets');
    this.loader.removeFuzzy('asset_versions');
    // this.loader.removeFuzzy('galleries'); // clearing last_changed_at and asset count
    this.allAssets.clear();
  }
}
